import { useState, useEffect } from 'react';
import { useUIStore } from '../../stores/uiStore';
import { useDeviceStore } from '../../stores/deviceStore';
import { useStorageStore } from '../../stores/storageStore';
import { useDeviceStatus, useRecordingStatus } from '../../stores/systemStore';
import { useEEGAnalysis, usePPGAnalysis } from '../../stores/processedDataStore';
import { Bluetooth, Eye, Database, Settings, FolderOpen, Battery, Activity } from 'lucide-react';
import { Card } from '../ui/card';
import { Pill } from '../ui/pill';
import { PageShell, PageHeader } from '../ui/PageShell';
import VisualizerPreviewCard from './VisualizerPreview/VisualizerPreviewCard';
import { bluetoothService } from '../../utils/bluetoothService';
import { useTranslation } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';

export default function Dashboard() {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const { setActiveMenu } = useUIStore();
  const { isConnected } = useDeviceStatus();
  const { isRecording } = useRecordingStatus();
  const { currentSession, storageStats } = useStorageStore();
  const connectedDevice = useDeviceStore(state => state.connectedDevice);
  const samplingRates = connectedDevice?.samplingRates || null;

  const [realtimeBatteryLevel, setRealtimeBatteryLevel] = useState<number>(0);
  const [recordingDuration, setRecordingDuration] = useState<string>('00:00:00');

  const eegAnalysis = useEEGAnalysis();
  const ppgAnalysis = usePPGAnalysis();

  const [stabilizedEEGValues, setStabilizedEEGValues] = useState<{
    arousal: number | null; valence: number | null; focus: number | null;
  }>({ arousal: null, valence: null, focus: null });

  const [stabilizedPPGValues, setStabilizedPPGValues] = useState<{
    heartRate: number | null; rmssd: number | null; sdnn: number | null; pnn50: number | null; stressIndex: number | null; spo2: number | null;
  }>({ heartRate: null, rmssd: null, sdnn: null, pnn50: null, stressIndex: null, spo2: null });

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording && currentSession?.startTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const start = new Date(currentSession.startTime).getTime();
        const diff = Math.floor((now - start) / 1000);
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        setRecordingDuration(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      }, 1000);
    } else { setRecordingDuration('00:00:00'); }
    return () => { if (interval) clearInterval(interval); };
  }, [isRecording, currentSession?.startTime]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isConnected && bluetoothService.isConnected()) {
      interval = setInterval(async () => {
        try { const batteryLevel = await bluetoothService.getBatteryLevel(); setRealtimeBatteryLevel(batteryLevel); }
        catch (error) { console.error('Failed to get realtime battery level:', error); }
      }, 1000);
    } else { setRealtimeBatteryLevel(0); }
    return () => { if (interval) clearInterval(interval); };
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;
    const interval = setInterval(() => {
      const eeg = eegAnalysis;
      const ppg = ppgAnalysis;
      if (eeg) {
        setStabilizedEEGValues({
          arousal: eeg.arousal ?? null,
          valence: eeg.valence ?? null,
          focus: eeg.focus ?? null,
        });
      }
      if (ppg) {
        setStabilizedPPGValues({
          heartRate: ppg.heartRate ?? null,
          rmssd: ppg.rmssd ?? null,
          sdnn: ppg.sdnn ?? null,
          pnn50: ppg.pnn50 ?? null,
          stressIndex: ppg.stressIndex ?? null,
          spo2: ppg.spo2 ?? null,
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isConnected, eegAnalysis, ppgAnalysis]);

  const handleNavigateToLinkBand = () => setActiveMenu('linkband');
  const handleOpenVisualizer = () => setActiveMenu('visualizer');
  const handleOpenDataCenter = () => setActiveMenu('datacenter');
  const handleChangeStorage = () => setActiveMenu('datacenter');

  const toPercent = (v: number | null): number => {
    if (v === null || !Number.isFinite(v)) return 0;
    const pct = v <= 1 ? v * 100 : v;
    return Math.max(0, Math.min(100, Math.round(pct)));
  };
  const focusPercent = toPercent(stabilizedEEGValues.focus);
  const relaxPercent = toPercent(stabilizedEEGValues.arousal);
  const hasEegData = stabilizedEEGValues.focus !== null || stabilizedEEGValues.arousal !== null;

  // DataCenter 스타일 StatCard — 우측 상단 아이콘 + font-medium
  const StatCard = ({ eyebrow, value, icon, sub }: { eyebrow: string; value: string; icon?: React.ReactNode; sub?: string }) => (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="lb-eyebrow">{eyebrow}</div>
          <div className="text-2xl font-medium t-strong mt-2">{value}</div>
          {sub && <p className="text-xs t-faint mt-1">{sub}</p>}
        </div>
        {icon && <div className="mt-0.5">{icon}</div>}
      </div>
    </Card>
  );

  return (
    <PageShell>
      {/* Page Header */}
      <PageHeader 
        title={t.dashboard.title || 'Dashboard'} 
        subtitle={t.dashboard.subtitle || ''}
      >
        <Pill 
          variant="secondary"
          size="sm"
        >
          <span className={isConnected ? 'lb-dot' : 'lb-dot lb-dot--idle'} style={{ marginRight: 6 }} />
          {isConnected ? t.dashboard.connected : t.dashboard.waiting}
        </Pill>
      </PageHeader>

      {/* Section 1: Device Status */}
      <Card className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
            <Bluetooth className="w-5 h-5 text-indigo-400" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-medium t-strong">{t.dashboard.linkBandStatus}</h3>
            <p className="text-sm t-soft">{t.dashboard.linkBandStatusSub}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard 
            eyebrow="BATTERY" 
            value={isConnected && realtimeBatteryLevel > 0 ? `${realtimeBatteryLevel}%` : '—'}
            icon={<Battery className="w-6 h-6 text-green-500" strokeWidth={1.5} />}
            sub={isConnected ? 'Connected' : 'Disconnected'} 
          />
          <StatCard 
            eyebrow="CONNECTION" 
            value={isConnected ? new Date().toLocaleTimeString() : '—'}
            icon={<Activity className="w-6 h-6 text-indigo-400" strokeWidth={1.5} />}
            sub={isConnected ? `${t.dashboard.connectionDuration} ${recordingDuration}` : t.dashboard.connectFromDeviceManager} 
          />
          <Card className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="lb-eyebrow">SAMPLING RATES</div>
                <div className="flex flex-col gap-1.5 mt-2">
                  <div className="flex justify-between text-sm gap-4">
                    <span className="t-soft">EEG</span>
                    <span className="font-medium">{isConnected && samplingRates?.eeg ? `${samplingRates.eeg.toFixed(1)} Hz` : '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm gap-4">
                    <span className="t-soft">PPG</span>
                    <span className="font-medium">{isConnected && samplingRates?.ppg ? `${samplingRates.ppg.toFixed(1)} Hz` : '—'}</span>
                  </div>
                  <div className="flex justify-between text-sm gap-4">
                    <span className="t-soft">ACC</span>
                    <span className="font-medium">{isConnected && samplingRates?.acc ? `${samplingRates.acc.toFixed(1)} Hz` : '—'}</span>
                  </div>
                </div>
              </div>
              <Activity className="w-6 h-6 text-indigo-400" strokeWidth={1.5} />
            </div>
          </Card>
        </div>
        <div className="mt-4">
          <button className="lb-btn lb-btn--primary" onClick={handleNavigateToLinkBand} style={{ width: '100%', justifyContent: 'center' }}>
            <Bluetooth strokeWidth={1.5} size={18} style={{ marginRight: 8 }} />
            {t.dashboard.goToLinkBand}
          </button>
        </div>
      </Card>

      {/* Section 2: Visualizer Preview */}
      <Card className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
            <Eye className="w-5 h-5 text-green-400" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-medium t-strong">{t.dashboard.dataVisualization}</h3>
            <p className="text-sm t-soft">{t.dashboard.dataVisualizationSub}</p>
          </div>
        </div>
        <VisualizerPreviewCard onOpenVisualizer={handleOpenVisualizer} />
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 mt-5">
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>FOCUS</span>
              <span style={{ fontSize: 11, color: 'var(--lb-indigo-400)' }}>{hasEegData ? `${focusPercent}%` : '—'}</span>
            </div>
            <div style={{ height: 4, borderRadius: 999, background: 'var(--lb-white-06)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${focusPercent}%`, borderRadius: 999, background: 'var(--lb-indigo-500)', transition: 'width 500ms' }} />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>RELAX</span>
              <span style={{ fontSize: 11, color: 'var(--lb-signal-acc)' }}>{hasEegData ? `${relaxPercent}%` : '—'}</span>
            </div>
            <div style={{ height: 4, borderRadius: 999, background: 'var(--lb-white-06)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${relaxPercent}%`, borderRadius: 999, background: 'var(--lb-signal-acc)', transition: 'width 500ms' }} />
            </div>
          </div>
        </div>
      </Card>

      {/* Section 3: Storage */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Database className="w-5 h-5 text-green-500" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-medium t-strong">{t.dashboard.dataStorage}</h3>
              <p className="text-sm t-soft">{t.dashboard.dataStorageSub}</p>
            </div>
          </div>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={handleChangeStorage}>
            <Settings strokeWidth={1.5} size={14} style={{ marginRight: 6 }} />{t.dashboard.settings}
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard 
            eyebrow="USED" 
            value={storageStats.used} 
            icon={<Database className="w-6 h-6 text-orange-500" strokeWidth={1.5} />}
          />
          <StatCard 
            eyebrow="AVAILABLE" 
            value={storageStats.available} 
            icon={<Database className="w-6 h-6 text-white/40" strokeWidth={1.5} />}
          />
          <StatCard 
            eyebrow="SESSIONS" 
            value={String(storageStats.sessions)} 
            icon={<Database className="w-6 h-6 text-indigo-400" strokeWidth={1.5} />}
          />
          <StatCard 
            eyebrow="TOTAL" 
            value={storageStats.totalSize} 
            icon={<Database className="w-6 h-6 text-green-500" strokeWidth={1.5} />}
          />
        </div>
        <div className="mt-4">
          <button className="lb-btn lb-btn--primary" onClick={handleOpenDataCenter} style={{ width: '100%', justifyContent: 'center' }}>
            <FolderOpen strokeWidth={1.5} size={18} style={{ marginRight: 8 }} />
            {t.dashboard.goToDataCenter}
          </button>
        </div>
      </Card>
    </PageShell>
  );
}
